import { create } from 'zustand';

import { getChatNoteDetail, getNoteDetail } from '@/service/note';
import type {
  MemoryListChatItem,
  MemoryListItem,
  MemoryListNoteItem,
  MemoryListSerendipityItem,
  MemoryListSerendipityList,
  MemoryListTodoItem
} from '@/service/note';
import type { SerendipityItem } from '@/service/serendipity';
import { getTodoDetail } from '@/service/todo';

interface NoteItemStore {
  noteItemList: MemoryListItem[];
  updateNoteItemList: (noteList: MemoryListItem[]) => void;
  updateSingleNoteItem: (note: MemoryListItem) => void;
  updateTodoNote: (id: number) => Promise<void>;
  updateNote: (noteId: string) => void;
  updateChatNote: (sessionId: string) => void;
  updateSerendipityItem: (
    value: SerendipityItem,
    propertyToUpdate?: keyof SerendipityItem
  ) => unknown;
  deleteItemIds: number[];
  addDeleteItemId: (id: number) => void;
  removeDeleteItemId: (id: number) => void;
  addDeleteItemIdByChatSession: (sessionId: string) => void;
  unreadIds: string[];
  removeUnreadId: (id: string) => void;
  addUnreadId: (id: string) => void;
}

const useNoteItemStore = create<NoteItemStore>()((set, get) => ({
  noteItemList: [] as MemoryListItem[],
  deleteItemIds: [],
  unreadIds: [],
  removeUnreadId: (id: string) => {
    const { unreadIds, noteItemList, updateSingleNoteItem } = get();
    const newUnreadIds = unreadIds.filter((item) => item !== id);
    const note = noteItemList.find((item) => item.id == parseInt(id));

    if (note) {
      note.unread = false;

      updateSingleNoteItem(note);
    }

    set({ unreadIds: newUnreadIds });
  },
  addUnreadId: (id: string) => {
    const { unreadIds } = get();

    set({ unreadIds: [...unreadIds, id] });
  },
  addDeleteItemId: (id: number) => {
    const { deleteItemIds, noteItemList, updateSerendipityItem } = get();

    const _deleteItemIds = [...deleteItemIds, id];

    noteItemList.forEach((item) => {
      // 更新serendipity的savedNoteId
      if (item.type === 'serendipity' && item.serendipity.savedNoteId == id) {
        const _serendipity = { ...item.serendipity, savedNoteId: undefined };

        updateSerendipityItem(_serendipity);
      }
    });

    set({ deleteItemIds: _deleteItemIds });
  },
  removeDeleteItemId: (id: number) => {
    const { deleteItemIds } = get();
    const newDeleteItemIds = deleteItemIds.filter((deleteId) => deleteId !== id);

    set({ deleteItemIds: newDeleteItemIds });
  },
  updateNoteItemList: (_noteItemList: MemoryListItem[]) => {
    const { noteItemList, unreadIds } = get();
    const newNoteItemList = [...noteItemList];

    // 更新列表的同时，将本地的删除list清空，同步远端数据
    set({ deleteItemIds: [] });

    _noteItemList.forEach((newItem) => {
      const index = newNoteItemList.findIndex(
        (oldItem) => oldItem.id === newItem.id && oldItem.type === newItem.type
      );

      if (newItem.id && unreadIds.includes(newItem.id.toString())) {
        newItem.unread = true;
      }

      if (index === -1) {
        newNoteItemList.push(newItem);
      } else {
        newNoteItemList[index] = newItem;
      }
    });

    set({ noteItemList: newNoteItemList });
  },
  updateSingleNoteItem: (_note: MemoryListItem) => {
    const { noteItemList } = get();
    const newNoteItemList = [...noteItemList];
    const index = newNoteItemList.findIndex(
      (oldItem) => oldItem.id == _note.id && oldItem.type == _note.type
    );

    if (index === -1) {
      newNoteItemList.push(_note);
    } else {
      newNoteItemList[index] = _note;
    }

    set({ noteItemList: newNoteItemList });
  },
  updateTodoNote: (id: number) => {
    return getTodoDetail({ id }).then((res) => {
      if (res.data.code === 0) {
        const { updateSingleNoteItem } = get();
        const todo = res.data.data;
        const todoNoteItem: MemoryListTodoItem = {
          actor: 'user',
          id: todo.id,
          type: 'todo',
          sortTimestamp: todo.createTime,
          deadline: todo.deadline,
          todo: todo
        };

        updateSingleNoteItem(todoNoteItem);
      }
    });
  },
  updateNote: (noteId: string) => {
    const { noteItemList } = get();
    const originNote = noteItemList.find(
      (item) => item.id == Number(noteId) && item.type == 'note'
    ) as MemoryListNoteItem | undefined;

    getNoteDetail(noteId).then((res) => {
      if (res.data.code === 0) {
        const { updateSingleNoteItem } = get();
        const note = res.data.data;

        if (!note.noteId) return;

        const noteItem: MemoryListNoteItem = originNote
          ? {
              ...originNote,
              actor: 'user',
              note: {
                ...originNote.note,
                ...note
              }
            }
          : {
              type: 'note',
              actor: 'user',
              unread: true,
              sortTimestamp: note.createTime || note.createTimestamp || Date.now(),
              id: Number(note.noteId),
              note
            };

        updateSingleNoteItem(noteItem);
      }
    });
  },
  updateChatNote: (sessionId: string) => {
    const { noteItemList } = get();
    const originNote = noteItemList.find(
      (item) => item.type === 'chat' && item.chat.sessionId === sessionId
    ) as MemoryListChatItem;

    if (!originNote) return;

    getChatNoteDetail({ sessionId }).then((res) => {
      if (res.data.code === 0) {
        const { updateSingleNoteItem } = get();
        const chat = res.data.data;

        const noteItem: MemoryListChatItem = {
          ...originNote,
          chat: { ...originNote.chat, ...chat }
        };

        updateSingleNoteItem(noteItem);
      }
    });
  },
  updateSerendipityItem: (value: SerendipityItem, propertyToUpdate?: keyof SerendipityItem) => {
    const { noteItemList } = get();
    const newList = [...noteItemList];
    const index = newList.findIndex(
      (oldItem) => oldItem.type === 'serendipity' && oldItem.serendipity.id == value.id
    );

    if (index !== -1) {
      const currentItem = newList[index] as MemoryListSerendipityItem;

      if (propertyToUpdate) {
        // If a specific property is specified, only update that property
        (newList[index] as MemoryListSerendipityItem).serendipity = {
          ...currentItem.serendipity,
          [propertyToUpdate]: value[propertyToUpdate]
        };
      } else {
        // Otherwise, update all properties as before
        (newList[index] as MemoryListSerendipityItem).serendipity = {
          ...currentItem.serendipity,
          ...value
        };
      }
    }

    // 更新serendipityList
    const _index = newList.findIndex(
      (oldItem) =>
        oldItem.type === 'serendipityList' &&
        oldItem.serendipityList.some((item) => item.serendipity.id === value.id)
    );

    if (_index !== -1) {
      const _currentItem = newList[_index] as MemoryListSerendipityList;

      (newList[_index] as MemoryListSerendipityList).serendipityList =
        _currentItem.serendipityList.map((item) => {
          if (item.serendipity.id == value.id) {
            if (propertyToUpdate) {
              return {
                ...item,
                serendipity: { ...item.serendipity, [propertyToUpdate]: value[propertyToUpdate] }
              };
            }

            return { ...item, serendipity: { ...item.serendipity, ...value } };
          }

          return item;
        });
    }

    set({ noteItemList: newList });
  },
  addDeleteItemIdByChatSession: (sessionId: string) => {
    const { noteItemList } = get();
    const chatNote = noteItemList.find(
      (item) => item.type === 'chat' && item.chat.sessionId == sessionId
    ) as MemoryListChatItem;

    if (chatNote.id) {
      const { addDeleteItemId } = get();

      addDeleteItemId(chatNote.id);
    }
  }
}));

export default useNoteItemStore;
